import { useRef, useState, useEffect } from 'react';
import { Popper as MuiPopper, Paper, List, ListItem, ListItemButton, ListItemIcon, ListItemText, CircularProgress, Box, ClickAwayListener } from '@mui/material';
import './styles.scss';

export default function Popper({ controlRef, children }) {
    const [open, setOpen] = useState(false);
    const [loadingItemId, setLoadingItemId] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);

    const configRef = useRef();

    const popperRef = useRef();

    // Configurar o controlRef com os métodos open e close
    useEffect(() => {
        if (controlRef) {
            controlRef.current = {
                /**
                 * Abre o popper com as opções especificadas
                 * @param {*} options in format [{id, label, icon,variant:danger,success,warning,info,,null, disabled, onClick}]
                 * @param {*} side 
                 * @param {*} anchorElement - elemento de referência para posicionamento
                 */
                open(options, side = "bottom", anchorElement="bottom") {
                    configRef.current = {
                        options,
                        side
                    };
                    setAnchorEl(anchorElement);
                    setOpen(true);
                },

                close() {
                    setOpen(false);
                    setLoadingItemId(null);
                    setAnchorEl(null);
                }
            };
        }
    }, [controlRef]);

    // Função para lidar com o clique em uma opção
    const handleOptionClick = async (option) => {
        if (option.disabled || loadingItemId !== null || !option.onClick) return;

        try {
            setLoadingItemId(option.id);
            await option.onClick();
        } catch (error) {
            console.error('Erro ao executar onClick:', error);
        } finally {
            setLoadingItemId(null);
        }
    };

    // Função para renderizar o ícone da opção
    const renderIcon = (option) => {
        if (option.icon) {
            return (
                <ListItemIcon
                    sx={{
                        minWidth: 36,
                        color: 'inherit'
                    }}
                >
                    {option.icon}
                </ListItemIcon>
            );
        }
        return null;
    };



    // Função para renderizar o loader
    const renderLoader = (option) => {
        if (loadingItemId === option.id) {
            return (
                <CircularProgress
                    size={16}
                    sx={{
                        ml: 1,
                        color: 'rgba(255, 255, 255, 0.7)'
                    }}
                />
            );
        }
        return null;
    };

    return (
        <>
            {children}
            <MuiPopper
                open={open && !!anchorEl}
                anchorEl={anchorEl}
                placement={configRef.current?.side || "bottom"}
                className="custom-popper"
                modifiers={[
                    {
                        name: 'offset',
                        options: {
                            offset: [0, 8],
                        },
                    },
                    {
                        name: 'preventOverflow',
                        options: {
                            boundary: 'viewport',
                            padding: 8,
                        },
                    },
                    {
                        name: 'flip',
                        options: {
                            fallbackPlacements: ['top', 'bottom'],
                        },
                    },
                    {
                        name: 'shift',
                        options: {
                            padding: 8,
                        },
                    },
                ]}
            >
                <ClickAwayListener onClickAway={() => {
                    setOpen(false);
                    setAnchorEl(null);
                }}>
                    <Paper
                        elevation={8}
                        sx={{
                            backgroundColor: '#1e1e1e',
                            borderRadius: '18px',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            overflow: 'hidden',
                            minWidth: 200,
                            maxWidth: 'min(300px, calc(100vw - 32px))',
                            maxHeight: '320px',
                            width: 'auto',
                        }}
                    >
                        <List sx={{
                            p: 0,
                            maxHeight: '320px',
                            overflow: 'auto',
                            '&::-webkit-scrollbar': {
                                display: 'none'
                            },
                            scrollbarWidth: 'none',
                            msOverflowStyle: 'none'
                        }}>
                            {configRef.current?.options?.map((option, index) => (
                                <ListItem
                                    key={option.id || index}
                                    disablePadding
                                    sx={{
                                        borderBottom: index < configRef.current.options.length - 1
                                            ? '1px solid rgba(255, 255, 255, 0.1)'
                                            : 'none'
                                    }}
                                >
                                    <ListItemButton
                                        onClick={() => handleOptionClick(option)}
                                        disabled={option.disabled || loadingItemId !== null}
                                        className={`popper-option-${option.variant || 'default'}`}
                                        sx={{
                                            py: 1.1,
                                            px: 2,
                                            color: option.disabled ? 'rgba(255, 255, 255, 0.4)' : 'rgba(255, 255, 255, 0.9)',
                                            backgroundColor: 'transparent',
                                            '&:hover': {
                                                backgroundColor: option.disabled ? 'transparent' : 'rgba(255, 255, 255, 0.08)',
                                            },
                                            '&.Mui-disabled': {
                                                opacity: 0.5,
                                            }
                                        }}
                                    >
                                        {renderIcon(option)}
                                        <ListItemText
                                            primary={option.label}
                                            sx={{
                                                '& .MuiListItemText-primary': {
                                                    fontSize: '14px',
                                                    fontWeight: 500,
                                                }
                                            }}
                                        />
                                        {renderLoader(option)}
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                    </Paper>
                </ClickAwayListener>
            </MuiPopper>
        </>
    );
}
